import { useEffect, useMemo, useState } from 'react';
import { Heart } from 'lucide-react';
import { PreviewPlayer } from '../components/player/PreviewPlayer';
import { useAuth } from '../features/auth/AuthContext';
import { mixProducts, type MixProduct } from '../data/mixProducts';

function formatDate(date: string) {
  return new Date(date).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' });
}

export function Mixes() {
  const { user } = useAuth();
  const activeMixes = useMemo(() => mixProducts.filter((mix) => mix.isActive), []);
  const [playerTitle, setPlayerTitle] = useState('Select a mix to preview');
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const mixWishlistKey = user ? `primetime.mixWishlist.${user.id}` : '';
  const [mixWishlistIds, setMixWishlistIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!user) {
      setMixWishlistIds(new Set());
      return;
    }

    try {
      const saved = localStorage.getItem(`primetime.mixWishlist.${user.id}`);
      setMixWishlistIds(new Set(saved ? JSON.parse(saved) : []));
    } catch {
      setMixWishlistIds(new Set());
    }
  }, [user]);

  function playPreview(mix: MixProduct) {
    setPlayerTitle(`${mix.title} - ${mix.creator}`);
    setActiveVideoUrl(mix.previewUrl || null);
  }

  function toggleMixWishlist(mix: MixProduct) {
    if (!user) return;

    setMixWishlistIds(current => {
      const next = new Set(current);
      if (next.has(mix.id)) {
        next.delete(mix.id);
      } else {
        next.add(mix.id);
      }
      localStorage.setItem(mixWishlistKey, JSON.stringify([...next]));
      return next;
    });
  }

  return (
    <>
      <PreviewPlayer title={playerTitle} subtitle="Click a mix title to preview" videoUrl={activeVideoUrl} />
      <section className="container-shell pb-14 pt-6">
        <div className="mb-8">
          <p className="text-sm font-black uppercase tracking-[0.35em] text-prime-red">Video Mixes</p>
          <h1 className="mt-4 text-4xl font-black text-white">Mixes Catalog</h1>
          <p className="mt-3 max-w-2xl text-white/60">Standalone video/audio mixes for set prep, transitions, breaks, and event moments.</p>
        </div>

        <main className="min-w-0">
          <div className="glass min-h-[600px] overflow-hidden rounded-3xl">
            <div className="custom-scrollbar">
              <div className="grid w-full grid-cols-[minmax(0,1fr)_96px] items-center gap-4 border-b border-white/10 bg-white/[0.035] px-4 py-4 text-left text-[10px] font-black uppercase tracking-[0.16em] text-white/50 md:grid-cols-[minmax(0,1.8fr)_minmax(0,1.2fr)_96px_72px_110px_112px_120px] md:px-6">
                <div>Track / Version</div>
                <div className="hidden md:block">Artist</div>
                <div className="hidden md:block">BPM</div>
                <div className="hidden md:block">Key</div>
                <div className="hidden md:block">Genre</div>
                <div className="hidden md:block">Date</div>
                <div className="text-right">Unlock</div>
              </div>

              <div className="w-full divide-y divide-white/5">
                {activeMixes.map((mix) => {
                  const isSaved = mixWishlistIds.has(mix.id);

                  return (
                    <article key={mix.id} className="grid w-full grid-cols-[minmax(0,1fr)_96px] items-center gap-4 px-4 py-4 transition-colors group hover:bg-white/[0.03] md:grid-cols-[minmax(0,1.8fr)_minmax(0,1.2fr)_96px_72px_110px_112px_120px] md:px-6 md:py-5">
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => playPreview(mix)}
                          aria-label={`Preview ${mix.title} by ${mix.creator}`}
                          className="focus-red block max-w-full rounded-md text-left font-bold text-white"
                        >
                          <span className="truncate transition-colors group-hover:text-prime-red group-focus-within:text-prime-red">
                            {mix.title} <span className="font-medium text-white/55">{mix.durationMinutes}-Minute Video Mix</span>
                          </span>
                        </button>
                        <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-wider text-white/35 md:hidden">
                          <span>{mix.bpmLabel}</span>
                          <span>{mix.genre === 'Open Format' || mix.genre === 'Lounge' ? '' : mix.genre}</span>
                          <span>{formatDate(mix.releaseDate)}</span>
                        </div>
                      </div>

                      <div className="hidden min-w-0 text-xs font-bold uppercase tracking-wider text-white/40 md:block">
                        <div className="truncate">{mix.creator}</div>
                      </div>

                      <div className="hidden text-sm font-black text-white/80 md:block">
                        <div>{mix.bpmLabel}</div>
                      </div>

                      <div className="hidden text-sm font-black text-white/70 md:block">--</div>

                      <div className="hidden min-w-0 text-xs font-medium text-white/60 md:block">
                        <div className="truncate">{mix.genre === 'Open Format' || mix.genre === 'Lounge' ? '' : mix.genre}</div>
                      </div>

                      <div className="hidden min-w-0 text-xs font-medium text-white/60 md:block">
                        <div className="truncate">{formatDate(mix.releaseDate)}</div>
                      </div>

                      <div className="flex items-center justify-end gap-2">
                        {user && (
                          <button
                            type="button"
                            onClick={() => toggleMixWishlist(mix)}
                            className={`focus-red flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-all md:h-10 md:w-10 md:rounded-xl ${isSaved ? 'border-prime-red bg-prime-red/15 text-prime-red shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:border-prime-red/50 hover:text-prime-red'}`}
                            aria-label={isSaved ? 'Remove mix from wishlist' : 'Add mix to wishlist'}
                          >
                            <Heart size={15} fill={isSaved ? 'currentColor' : 'none'} />
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={true}
                          className={`btn-primary flex min-w-[86px] items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] shadow-none opacity-50 cursor-not-allowed transition-all md:min-w-[96px] md:rounded-xl md:px-4 md:py-2 md:text-xs`}
                        >
                          Mix downloads coming soon
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      </section>
    </>
  );
}
